import * as React from "react";
import { createRoot } from "react-dom/client";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import "@fontsource/lato";
import "@fontsource/zilla-slab";
import App from "./App";
import theme from "./theme";
import { GlobalProvider } from "./context/GlobalContext";
import { SnackbarProvider } from "notistack";
import * as Sentry from "@sentry/react";
import ErrorPage from "./ErrorPage";
const apiUrl = import.meta.env.VITE_AUTHOR_PLATFORM_API_URL;

// Determine environment based on current hostname
const determineEnvironment = () => {
  const hostname = window.location.hostname;
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("dev.")
  ) {
    return "development";
  } else if (hostname.includes("staging")) {
    return "staging";
  } else {
    return "production";
  }
};

const environment = determineEnvironment();

// Create the API domain regex pattern for tracing
const apiDomain = new URL(apiUrl).hostname;
const apiTracePattern = new RegExp(
  `^https?:\/\/${apiDomain.replace(".", "\\.")}`
);

// Custom integration to capture console logs in production
// class ConsoleIntegration {
//   name = "console";
//   setupOnce() {
//     if (environment !== "production") return;

//     // Save original console methods
//     const originalConsole = {
//       log: console.log,
//       warn: console.warn,
//       error: console.error,
//       info: console.info,
//       debug: console.debug,
//     };

//     // Override console methods to send to Sentry
//     console.log = (...args) => {
//       Sentry.addBreadcrumb({
//         category: "console",
//         message: args.map((arg) => String(arg)).join(" "),
//         level: "info",
//       });
//       originalConsole.log(...args);
//     };

//     console.warn = (...args) => {
//       Sentry.addBreadcrumb({
//         category: "console",
//         message: args.map((arg) => String(arg)).join(" "),
//         level: "warning",
//       });
//       originalConsole.warn(...args);
//     };

//     console.error = (...args) => {
//       Sentry.addBreadcrumb({
//         category: "console",
//         message: args.map((arg) => String(arg)).join(" "),
//         level: "error",
//       });
//       originalConsole.error(...args);
//     };

//     console.info = (...args) => {
//       Sentry.addBreadcrumb({
//         category: "console",
//         message: args.map((arg) => String(arg)).join(" "),
//         level: "info",
//       });
//       originalConsole.info(...args);
//     };

//     console.debug = (...args) => {
//       Sentry.addBreadcrumb({
//         category: "console",
//         message: args.map((arg) => String(arg)).join(" "),
//         level: "debug",
//       });
//       originalConsole.debug(...args);
//     };
//   }
// }
if (environment === "production") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_SENTRY_RELEASE || "unknown",
    environment: environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      // Add our custom console integration
      //new ConsoleIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", apiTracePattern],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}
console.info(
  "Storywise Authors Release: ",
  import.meta.env.VITE_SENTRY_RELEASE
);

const rootElement = document.getElementById("root");
const root = createRoot(rootElement);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <GlobalProvider>
          <SnackbarProvider maxSnack={3}>
            <App />
          </SnackbarProvider>
        </GlobalProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
